import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button, Alert } from "@mui/material";

import {
  useAssetCreationDataQuery,
  useCreateAssetMutation,
  useGetOwnerAssetByIdQuery,
  useUpdateOwnerAssetMutation,
} from "../../../redux/api/authApi";
import { toast } from "../../common/AppToast";
import { mergeAssetCreationData } from "./listNewAssetCreationData";
import {
  buildCreateAssetFormData,
  buildPartialUpdateAssetFormData,
  initialAssetDraft,
} from "./listNewAssetDraft";
import { mapOwnerApiAssetToDraft } from "./listNewAssetEditMapper";

import ListNewAssetStep1 from "./ListNewAssetStep1";
import ListNewAssetStep2 from "./ListNewAssetStep2";
import ListNewAssetStep2NonRealEstate from "./ListNewAssetStep2NonRealEstate";
import ListNewAssetStep3 from "./ListNewAssetStep3";
import ListNewAssetStep4 from "./ListNewAssetStep4";
import ListNewAssetStep5 from "./ListNewAssetStep5";
import ListNewAssetStep6 from "./ListNewAssetStep6";
import ListNewAssetTokenConfiguration from "./ListNewAssetTokenConfiguration";
import ListNewAssetStep7 from "./ListNewAssetStep7";

function isRealEstateCategoryTitle(title) {
  return String(title || "").trim() === "Real Estate";
}

export default function ListNewAsset() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editAssetId = searchParams.get("edit")?.trim() || "";
  const isEditMode = Boolean(editAssetId);

  const [step, setStep] = useState(() => (editAssetId ? 2 : 1));
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [assetDraft, setAssetDraft] = useState(() => initialAssetDraft());
  const editHydrateKeyRef = useRef("");
  /** Snapshot of wizard state when edit mode loaded — used to diff PATCH payload. */
  const editBaselineDraftRef = useRef(null);

  const [createAsset, { isLoading: isCreating }] = useCreateAssetMutation();
  const [updateOwnerAsset, { isLoading: isUpdating }] = useUpdateOwnerAssetMutation();
  const isSubmitting = isCreating || isUpdating;

  const { data: creationApiResponse } = useAssetCreationDataQuery();
  const creationData = useMemo(
    () => mergeAssetCreationData(creationApiResponse),
    [creationApiResponse]
  );

  const {
    data: ownerAssetRes,
    isError: ownerAssetError,
    error: ownerAssetErr,
    refetch: refetchOwnerAsset,
  } = useGetOwnerAssetByIdQuery(editAssetId, {
    skip: !editAssetId,
    /** Fresh GET after each visit (e.g. re-open edit after save) — avoids stale cache + stuck hydration. */
    refetchOnMountOrArgChange: true,
  });

  const ownerAssetDetail = ownerAssetRes?.data?.data ?? ownerAssetRes?.data;

  useEffect(() => {
    if (!editAssetId) {
      editHydrateKeyRef.current = "";
      editBaselineDraftRef.current = null;
      return;
    }
    // Opening edit (or switching asset): always allow the hydrate effect to run once for this load.
    editHydrateKeyRef.current = "";
  }, [editAssetId]);

  useEffect(() => {
    if (!editAssetId) return;
    if (!ownerAssetDetail || typeof ownerAssetDetail !== "object") return;

    const hydrateKey = `${editAssetId}:${ownerAssetDetail.updatedAt || ownerAssetDetail._id || ""}`;
    if (editHydrateKeyRef.current === hydrateKey) return;
    editHydrateKeyRef.current = hydrateKey;

    const mapped = mapOwnerApiAssetToDraft(ownerAssetDetail, creationData);
    const fullDraft = { ...initialAssetDraft(), ...mapped };
    const snapshot = JSON.parse(JSON.stringify(fullDraft));
    editBaselineDraftRef.current = snapshot;
    setAssetDraft(snapshot);
    setSelectedCategory(
      mapped.categoryId
        ? { id: mapped.categoryId, title: mapped.categoryTitle || "" }
        : null
    );
    setStep(2);
  }, [editAssetId, ownerAssetDetail, creationData]);

  const onDraftChange = useCallback((patch) => {
    setAssetDraft((prev) => ({ ...prev, ...patch }));
  }, []);

  const flowTitle =
    selectedCategory?.title || assetDraft?.categoryTitle || "";

  /** Empty title (e.g. step 1): default to 8-step layout until a category is chosen */
  const wizardTotalSteps =
    !flowTitle ? 8 : isRealEstateCategoryTitle(flowTitle) ? 8 : 7;
  const skipSpv = Boolean(flowTitle) && !isRealEstateCategoryTitle(flowTitle);

  const maxStepForNext = (categoryFromClick) => {
    const t =
      categoryFromClick?.title ||
      selectedCategory?.title ||
      assetDraft.categoryTitle ||
      "";
    return isRealEstateCategoryTitle(t) ? 8 : 7;
  };

  const nextStep = (category) => {
    if (category) {
      setSelectedCategory(category);
      setAssetDraft((prev) => ({
        ...prev,
        categoryId: category.id,
        categoryTitle: category.title,
      }));
    }
    const max = maxStepForNext(category);
    setStep((prev) => Math.min(prev + 1, max));
  };

  const prevStep = () => {
    if (editAssetId && step === 2) {
      navigate("/asset/listings");
      return;
    }
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmitAsset = async () => {
    try {
      if (editAssetId) {
        const baseline = editBaselineDraftRef.current;
        if (baseline) {
          const { formData, isEmpty } = buildPartialUpdateAssetFormData(
            assetDraft,
            baseline,
            creationData
          );
          if (isEmpty) {
            toast.info("No changes to save.");
            return;
          }
          await updateOwnerAsset({ assetId: editAssetId, body: formData }).unwrap();
        } else {
          const formData = buildCreateAssetFormData(assetDraft, creationData, {
            forUpdate: true,
          });
          await updateOwnerAsset({ assetId: editAssetId, body: formData }).unwrap();
        }
      } else {
        const formData = buildCreateAssetFormData(assetDraft, creationData);
        await createAsset(formData).unwrap();
      }
      navigate("/asset/listings", { replace: true });
    } catch (err) {
      const msg =
        err?.data?.message ||
        err?.data?.error ||
        err?.error ||
        (typeof err?.data === "string" ? err.data : null) ||
        err?.message ||
        "Could not submit asset. Please try again.";
      toast.error(msg);
    }
  };

  const renderStep2ByCategory = () => {
    const catTitle = selectedCategory?.title || assetDraft.categoryTitle || "";
    if (isRealEstateCategoryTitle(catTitle)) {
      return (
        <ListNewAssetStep2
          draft={assetDraft}
          creationData={creationData}
          onDraftChange={onDraftChange}
          nextStep={nextStep}
          prevStep={prevStep}
          isEditMode={isEditMode}
        />
      );
    }

    return (
      <ListNewAssetStep2NonRealEstate
        draft={assetDraft}
        onDraftChange={onDraftChange}
        nextStep={nextStep}
        prevStep={prevStep}
        categoryTitle={catTitle}
        wizardTotalSteps={wizardTotalSteps}
      />
    );
  };

  const categoryChipLabel =
    assetDraft.categoryTitle || selectedCategory?.title || "Real Estate";

  if (editAssetId && ownerAssetError && !ownerAssetDetail) {
    return (
      <Box sx={{ p: 3, maxWidth: 560, mx: "auto" }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {ownerAssetErr?.data?.message ||
            ownerAssetErr?.error ||
            "Could not load this asset for editing."}
        </Alert>
        <Button variant="contained" onClick={() => refetchOwnerAsset()}>
          Retry
        </Button>
        <Button sx={{ ml: 1 }} onClick={() => navigate("/asset/listings")}>
          Back to listings
        </Button>
      </Box>
    );
  }

  return (
    <>
      {step === 1 && !isEditMode && <ListNewAssetStep1 nextStep={nextStep} />}
      {step === 2 && renderStep2ByCategory()}
      {skipSpv ? (
        <>
          {step === 3 && (
            <ListNewAssetStep3
              draft={assetDraft}
              onDraftChange={onDraftChange}
              nextStep={nextStep}
              prevStep={prevStep}
              wizardStep={3}
              wizardTotalSteps={7}
              categoryChipLabel={categoryChipLabel}
              isEditMode={isEditMode}
            />
          )}
          {step === 4 && (
            <ListNewAssetStep5
              draft={assetDraft}
              onDraftChange={onDraftChange}
              nextStep={nextStep}
              prevStep={prevStep}
              wizardStep={4}
              wizardTotalSteps={7}
              categoryChipLabel={categoryChipLabel}
              isEditMode={isEditMode}
            />
          )}
          {step === 5 && (
            <ListNewAssetStep6
              draft={assetDraft}
              onDraftChange={onDraftChange}
              nextStep={nextStep}
              prevStep={prevStep}
              wizardStep={5}
              wizardTotalSteps={7}
              categoryChipLabel={categoryChipLabel}
              isEditMode={isEditMode}
            />
          )}
          {step === 6 && (
            <ListNewAssetTokenConfiguration
              draft={assetDraft}
              onDraftChange={onDraftChange}
              nextStep={nextStep}
              prevStep={prevStep}
              wizardStep={6}
              wizardTotalSteps={7}
              categoryChipLabel={categoryChipLabel}
            />
          )}
          {step === 7 && (
            <ListNewAssetStep7
              draft={assetDraft}
              creationData={creationData}
              prevStep={prevStep}
              onSubmit={handleSubmitAsset}
              isSubmitting={isSubmitting}
              wizardStep={7}
              wizardTotalSteps={7}
              categoryChipLabel={categoryChipLabel}
            />
          )}
        </>
      ) : (
        <>
          {step === 3 && (
            <ListNewAssetStep3
              draft={assetDraft}
              onDraftChange={onDraftChange}
              nextStep={nextStep}
              prevStep={prevStep}
              wizardStep={3}
              wizardTotalSteps={8}
              categoryChipLabel={categoryChipLabel}
              isEditMode={isEditMode}
            />
          )}
          {step === 4 && (
            <ListNewAssetStep4
              draft={assetDraft}
              creationData={creationData}
              onDraftChange={onDraftChange}
              nextStep={nextStep}
              prevStep={prevStep}
              wizardStep={4}
              wizardTotalSteps={8}
              categoryChipLabel={categoryChipLabel}
              isEditMode={isEditMode}
            />
          )}
          {step === 5 && (
            <ListNewAssetStep5
              draft={assetDraft}
              onDraftChange={onDraftChange}
              nextStep={nextStep}
              prevStep={prevStep}
              wizardStep={5}
              wizardTotalSteps={8}
              categoryChipLabel={categoryChipLabel}
              isEditMode={isEditMode}
            />
          )}
          {step === 6 && (
            <ListNewAssetStep6
              draft={assetDraft}
              onDraftChange={onDraftChange}
              nextStep={nextStep}
              prevStep={prevStep}
              wizardStep={6}
              wizardTotalSteps={8}
              categoryChipLabel={categoryChipLabel}
              isEditMode={isEditMode}
            />
          )}
          {step === 7 && (
            <ListNewAssetTokenConfiguration
              draft={assetDraft}
              onDraftChange={onDraftChange}
              nextStep={nextStep}
              prevStep={prevStep}
              wizardStep={7}
              wizardTotalSteps={8}
              categoryChipLabel={categoryChipLabel}
            />
          )}
          {step === 8 && (
            <ListNewAssetStep7
              draft={assetDraft}
              creationData={creationData}
              prevStep={prevStep}
              onSubmit={handleSubmitAsset}
              isSubmitting={isSubmitting}
              wizardStep={8}
              wizardTotalSteps={8}
              categoryChipLabel={categoryChipLabel}
            />
          )}
        </>
      )}
    </>
  );
}
